'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter, useSearchParams } from '@/lib/router';
import { TargetIcon, BuildingsIcon, TrendUpIcon, CheckIcon, TrashIcon, CalendarIcon } from '@phosphor-icons/react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useFinance } from '@/hooks/useFinance';
import { track } from '@/lib/telemetry';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import type { LinkableEntity, GoalHealthStatus, GoalType } from '@/types';
import { ProjectionInline } from './metas/CreatePreview';
import { MoneyInput } from '@/components/ui/MoneyInput';

function getHealthLabel(status: GoalHealthStatus) {
  const map: Record<GoalHealthStatus, { label: string; color: string }> = {
    Saudavel: { label: 'Excelente', color: 'text-success-text' },
    Atencao: { label: 'Atenção', color: 'text-warning-text' },
    Critica: { label: 'Crítica', color: 'text-error-text' },
    Atrasada: { label: 'Atrasada', color: 'text-error-text' },
    Concluida: { label: 'Concluída', color: 'text-success-text' },
    Indefinida: { label: 'Indefinida', color: 'text-text-secondary' },
  };
  return map[status] ?? map.Indefinida;
}

function computeHealth(
  currentAmount: number,
  targetAmount: number,
  monthlyContribution: number,
  targetDate: string | null,
): GoalHealthStatus {
  if (currentAmount >= targetAmount) return 'Concluida';
  if (!targetDate || !monthlyContribution) return 'Indefinida';

  const target = new Date(targetDate);
  const now = new Date();
  const monthsRemaining = (target.getFullYear() - now.getFullYear()) * 12 + target.getMonth() - now.getMonth();

  if (monthsRemaining <= 0) return 'Atrasada';

  const remaining = targetAmount - currentAmount;
  const requiredMonthly = remaining / monthsRemaining;

  if (monthlyContribution >= requiredMonthly) return 'Saudavel';
  if (monthlyContribution >= requiredMonthly * 0.7) return 'Atencao';
  return 'Critica';
}

interface MetaFormViewProps {
  goalId?: string;
}

export function MetaFormView({ goalId }: MetaFormViewProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    fetchGoalDetail,
    fetchLinkableEntities,
    fetchGoalsOverview,
    createGoal,
    updateGoal,
    deleteGoal,
  } = useFinance();
  const [essentialMonthly, setEssentialMonthly] = useState(0);
  const [averageMonthlyExpense, setAverageMonthlyExpense] = useState(0);
  const formatCurrency = useFormatCurrency();

  const isEditing = !!goalId;

  const initialType: GoalType = (searchParams.get('type') as GoalType | null) ?? 'Savings';
  const [goalType, setGoalType] = useState<GoalType>(initialType);
  const [targetMonths, setTargetMonths] = useState<string>('6');
  const [initialBalance, setInitialBalance] = useState('');
  const [currentBalance, setCurrentBalance] = useState('');
  const [interestRateAnnual, setInterestRateAnnual] = useState('');

  const [name, setName] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [icon, setIcon] = useState('');
  const [monthlyContribution, setMonthlyContribution] = useState('');
  const [selectedEntityIds, setSelectedEntityIds] = useState<Set<string>>(new Set());
  const [linkableEntities, setLinkableEntities] = useState<LinkableEntity[]>([]);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [entities, overview] = await Promise.all([
        fetchLinkableEntities(),
        fetchGoalsOverview().catch(() => null),
      ]);
      if (overview?.essentialMonthly != null) setEssentialMonthly(overview.essentialMonthly);
      if (overview?.averageMonthlyExpense != null) setAverageMonthlyExpense(overview.averageMonthlyExpense);

      if (goalId) {
        const detail = await fetchGoalDetail(goalId);
        setName(detail.name);
        setTargetAmount(detail.targetAmount.toString());
        setTargetDate(detail.targetDate ? detail.targetDate.split('T')[0] : '');
        setIcon(detail.icon ?? '');
        setGoalType(detail.type ?? 'Savings');
        if (detail.targetMonths != null) setTargetMonths(String(detail.targetMonths));
        if (detail.initialBalance != null) setInitialBalance(String(detail.initialBalance));
        if (detail.currentBalance != null) setCurrentBalance(String(detail.currentBalance));
        if (detail.interestRateAnnual != null) setInterestRateAnnual(String(detail.interestRateAnnual));
        const contributionParam = searchParams.get('contribution');
        setMonthlyContribution(
          contributionParam ?? detail.monthlyContribution?.toString() ?? ''
        );
        const ids = new Set<string>();
        const missing: LinkableEntity[] = [];
        detail.linkedEntities.forEach((le) => {
          const id = le.accountId ?? le.investmentId;
          if (!id) return;
          ids.add(id);
          if (!entities.some((e) => e.id === id)) {
            missing.push({
              id,
              name: le.name,
              balance: le.balance,
              type: le.type,
              institution: null,
              linkedGoalId: goalId,
              linkedGoalName: detail.name,
              isWithdrawn: le.isWithdrawn,
            });
          }
        });
        setLinkableEntities([...entities, ...missing]);
        setSelectedEntityIds(ids);
      } else {
        setLinkableEntities(entities);
      }
    } catch {
      // error handled by hook
    } finally {
      setInitialLoaded(true);
    }
  }, [goalId, fetchGoalDetail, fetchLinkableEntities, fetchGoalsOverview, searchParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleEntity = (id: string) => {
    setSelectedEntityIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };


  const parsedTargetAmount = parseFloat(targetAmount.replace(/\./g, '').replace(',', '.')) || 0;
  const parsedMonthlyContribution = parseFloat(monthlyContribution.replace(/\./g, '').replace(',', '.')) || 0;

  const previewCurrentAmount = useMemo(() => {
    return linkableEntities
      .filter((e) => selectedEntityIds.has(e.id))
      .reduce((sum, e) => sum + e.balance, 0);
  }, [linkableEntities, selectedEntityIds]);

  const previewProgress = parsedTargetAmount > 0
    ? Math.min(Math.round((previewCurrentAmount / parsedTargetAmount) * 1000) / 10, 100)
    : 0;

  const previewHealth = computeHealth(
    previewCurrentAmount,
    parsedTargetAmount,
    parsedMonthlyContribution,
    targetDate || null,
  );

  const healthInfo = getHealthLabel(previewHealth);

  const previewNode = (
    <div className="rounded-2xl border border-border-subtle bg-surface p-4 shadow-none">
      <div>
        <div className="mb-5 flex items-start justify-between">
          <span className="text-2xs font-bold tracking-widest text-text-tertiary uppercase">
            Preview do Dashboard
          </span>
          <div className="flex size-10 items-center justify-center rounded-xl bg-surface-sunken text-text-primary">
            <TargetIcon className="size-5" weight="bold" />
          </div>
        </div>

        <div className="mb-5">
          <h2 className="mb-1 text-lg font-bold wrap-break-word text-text-primary sm:text-xl">
            {name || 'Nome da Meta'}
          </h2>
          <p className="text-sm text-text-secondary">
            {targetDate ? (
              <>
                Objetivo para{' '}
                <span className="font-semibold text-text-primary">
                  {new Date(targetDate + 'T00:00:00').toLocaleDateString('pt-BR', {
                    month: 'long',
                    year: 'numeric',
                  })}
                </span>
              </>
            ) : (
              'Sem prazo definido'
            )}
          </p>
        </div>

        <div className="mb-6 sm:mb-8">
          <ProjectionInline
            goalType={goalType}
            name={name}
            targetAmount={parsedTargetAmount}
            monthlyContribution={parsedMonthlyContribution}
            startingBalance={previewCurrentAmount}
            reserveMonths={parseInt(targetMonths, 10) || 0}
            essentialMonthly={averageMonthlyExpense > 0 ? averageMonthlyExpense : essentialMonthly}
            debtBalance={
              parseFloat(currentBalance.replace(/\./g, '').replace(',', '.')) ||
              parseFloat(initialBalance.replace(/\./g, '').replace(',', '.')) ||
              0
            }
            debtPayment={parsedMonthlyContribution}
            debtAnnualRate={(parseFloat(interestRateAnnual.replace(',', '.')) || 0) / 100}
          />
        </div>

        <div className="space-y-4">
          <div className="flex items-end justify-between gap-3">
            <div className="min-w-0">
              <p className="mb-1 text-2xs font-bold tracking-wider text-text-tertiary uppercase">
                Acumulado
              </p>
              <p className="truncate font-mono text-2xl font-bold text-text-primary tabular-nums sm:text-3xl">
                {formatCurrency(previewCurrentAmount)}
              </p>
            </div>
            <div className="min-w-0 text-right">
              <p className="mb-1 text-2xs font-bold tracking-wider text-text-tertiary uppercase">Meta</p>
              <p className="truncate font-mono text-sm font-semibold text-text-secondary tabular-nums">
                {parsedTargetAmount > 0
                  ? formatCurrency(parsedTargetAmount)
                  : 'R$ 0,00'}
              </p>
            </div>
          </div>

          <div className="relative h-2 w-full overflow-hidden rounded-full bg-surface-sunken">
            <div
              className="absolute top-0 left-0 h-full rounded-full bg-success transition-all duration-500 motion-reduce:transition-none"
              style={{ width: `${previewProgress}%` }}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="size-2 animate-pulse rounded-full bg-success" />
              <span className={`text-xs font-bold ${healthInfo.color}`}>
                Saúde: {healthInfo.label}
              </span>
            </div>
            <span className="font-mono text-xs font-bold text-text-primary tabular-nums">
              {previewProgress}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const handleSave = async () => {
    if (!name || parsedTargetAmount <= 0) return;

    setSaving(true);
    try {
      const linkedEntities = Array.from(selectedEntityIds).flatMap(
        (id): { accountId?: string; investmentId?: string }[] => {
          const entity = linkableEntities.find((e) => e.id === id);
          if (!entity) return [];
          return entity.type === 'account'
            ? [{ accountId: id }]
            : [{ investmentId: id }];
        },
      );

      const parsedTargetMonths = parseInt(targetMonths, 10);
      const parsedInitialBalance = parseFloat(initialBalance.replace(/\./g, '').replace(',', '.'));
      const parsedCurrentBalance = parseFloat(currentBalance.replace(/\./g, '').replace(',', '.'));
      const parsedInterestRate = parseFloat(interestRateAnnual.replace(',', '.'));

      const typeExtras = {
        type: goalType,
        targetMonths: goalType === 'Reserve' && !isNaN(parsedTargetMonths) ? parsedTargetMonths : undefined,
        initialBalance: goalType === 'DebtPayoff' && !isNaN(parsedInitialBalance) ? parsedInitialBalance : undefined,
        currentBalance: goalType === 'DebtPayoff' && !isNaN(parsedCurrentBalance) ? parsedCurrentBalance : undefined,
        interestRateAnnual: goalType === 'DebtPayoff' && !isNaN(parsedInterestRate) ? parsedInterestRate : undefined,
      };

      if (isEditing) {
        await updateGoal(goalId!, {
          name,
          targetAmount: parsedTargetAmount,
          targetDate: targetDate || undefined,
          clearTargetDate: !targetDate,
          icon: icon || undefined,
          clearIcon: !icon,
          monthlyContribution: parsedMonthlyContribution || undefined,
          clearMonthlyContribution: !parsedMonthlyContribution,
          linkedEntities,
          ...typeExtras,
        });
        track('action.goal.update', { via: 'edit' });
      } else {
        await createGoal({
          name,
          targetAmount: parsedTargetAmount,
          targetDate: targetDate || undefined,
          icon: icon || undefined,
          monthlyContribution: parsedMonthlyContribution || undefined,
          linkedEntities,
          ...typeExtras,
        });
        track('action.goal.create', { type: goalType, linked_entities_count: linkedEntities.length });
      }

      router.push('/metas');
    } catch {
      // error handled by hook
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!goalId) return;
    setSaving(true);
    try {
      await deleteGoal(goalId);
      track('action.goal.delete');
      setShowDeleteConfirm(false);
      router.push('/metas');
    } catch {
      // error handled
      setSaving(false);
    }
  };

  if (!initialLoaded) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="space-y-6 lg:col-span-7">
            <Skeleton className="h-64 rounded-2xl" />
            <Skeleton className="h-64 rounded-2xl" />
          </div>
          <Skeleton className="h-96 rounded-2xl lg:col-span-5" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <h1 className="text-2xl font-bold tracking-tight text-text-primary sm:text-3xl">
        {isEditing ? 'Editar Meta' : 'Nova Meta Financeira'}
      </h1>

      {/* Layout Grid */}
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-12 lg:gap-10">
        {/* Left: Form */}
        <div className="space-y-6 sm:space-y-8 lg:col-span-7">
          {/* Section 1: Basic Info */}
          <div className="space-y-5">
            <div className="mb-3 flex items-center gap-2">
              <span className="flex size-8 items-center justify-center rounded-full bg-surface-sunken text-sm font-bold text-text-secondary">
                01
              </span>
              <h3 className="text-lg font-bold text-text-primary">Definição da Meta</h3>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div className="col-span-2">
                <label className="mb-2 block text-sm font-semibold text-text-secondary">Tipo de meta</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['Savings', 'Reserve', 'DebtPayoff'] as GoalType[]).map((t) => {
                    const label = t === 'Savings' ? 'Poupança' : t === 'Reserve' ? 'Reserva' : 'Dívida';
                    const selected = goalType === t;
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setGoalType(t)}
                        className={`px-3 py-2 rounded-xl text-sm font-medium border transition ${
                          selected
                            ? 'bg-surface-inverse border-border-strong text-on-inverse'
                            : 'bg-surface border-border text-text-secondary hover:border-border-strong'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {goalType === 'Reserve' && (
                <div className="col-span-2">
                  <label className="mb-2 block text-sm font-semibold text-text-secondary">
                    Meses de cobertura
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={36}
                    value={targetMonths}
                    onChange={(e) => setTargetMonths(e.target.value)}
                    className="w-full rounded-xl border border-border bg-surface px-4 py-3 font-mono text-text-primary focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                  />
                  {(() => {
                    const avgBase = averageMonthlyExpense > 0 ? averageMonthlyExpense : essentialMonthly;
                    const months = parseInt(targetMonths, 10) || 6;
                    const suggestion = months * avgBase;
                    return avgBase > 0 ? (
                      <p className="mt-1.5 flex flex-wrap items-center gap-1 text-xs text-text-secondary">
                        <span>Sugestão ({averageMonthlyExpense > 0 ? 'média 6 meses' : 'orçamento'}):</span>
                        <button
                          type="button"
                          onClick={() => {
                            const [int, dec] = suggestion.toFixed(2).split('.');
                            setTargetAmount(int.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + dec);
                          }}
                          className="font-mono text-info-text underline hover:text-info-text"
                        >
                          {formatCurrency(suggestion)}
                        </button>
                        <span className="text-text-tertiary">
                          ({months} × {formatCurrency(avgBase)}/mês)
                        </span>
                      </p>
                    ) : null;
                  })()}
                </div>
              )}
              {goalType === 'DebtPayoff' && (
                <>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-text-secondary">Saldo inicial (R$)</label>
                    <MoneyInput
                      value={initialBalance}
                      onChange={(formatted) => setInitialBalance(formatted)}
                      placeholder="10.000,00"
                      className="w-full rounded-xl border border-border bg-surface py-3 pr-4 font-mono text-text-primary focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-text-secondary">Saldo atual (R$)</label>
                    <MoneyInput
                      value={currentBalance}
                      onChange={(formatted) => setCurrentBalance(formatted)}
                      placeholder="6.500,00"
                      className="w-full rounded-xl border border-border bg-surface py-3 pr-4 font-mono text-text-primary focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="mb-2 block text-sm font-semibold text-text-secondary">
                      Taxa de juros anual (%, opcional)
                    </label>
                    <input
                      type="text"
                      value={interestRateAnnual}
                      onChange={(e) => setInterestRateAnnual(e.target.value)}
                      placeholder="18"
                      className="w-full rounded-xl border border-border bg-surface px-4 py-3 font-mono text-text-primary focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                    />
                  </div>
                </>
              )}
              <div className="col-span-2">
                <label className="mb-2 block text-sm font-semibold text-text-secondary">
                  Nome da Meta
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Liberdade Financeira ou Viagem Japão"
                  className="w-full rounded-xl border border-border bg-surface px-4 py-3 text-text-primary placeholder:text-text-tertiary focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold text-text-secondary">
                  Valor Alvo (R$)
                </label>
                <MoneyInput
                  value={targetAmount}
                  onChange={(formatted) => setTargetAmount(formatted)}
                  placeholder="50.000,00"
                  className="w-full rounded-xl border border-border bg-surface py-3 pr-4 font-mono text-text-primary placeholder:font-normal placeholder:text-text-tertiary focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold text-text-secondary">
                  Data de Conquista
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className={`w-full flex items-center justify-between bg-surface border border-border rounded-xl px-4 py-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary ${targetDate ? 'text-text-primary' : 'text-text-tertiary'}`}
                    >
                      <span>
                        {targetDate
                          ? format(new Date(targetDate + 'T12:00:00'), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
                          : 'Selecionar data...'}
                      </span>
                      <CalendarIcon size={16} className="shrink-0 text-text-tertiary" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" sideOffset={6} className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={targetDate ? new Date(targetDate + 'T12:00:00') : undefined}
                      onSelect={(date) => { if (date) setTargetDate(format(date, 'yyyy-MM-dd')); }}
                      disabled={{ before: new Date() }}
                      locale={ptBR}
                      numberOfMonths={1}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="col-span-2">
                <label className="mb-2 block text-sm font-semibold text-text-secondary">
                  Aporte Mensal Planejado (R$)
                </label>
                <MoneyInput
                  value={monthlyContribution}
                  onChange={(formatted) => setMonthlyContribution(formatted)}
                  placeholder="1.000,00"
                  className="w-full rounded-xl border border-border bg-surface py-3 pr-4 font-mono text-text-primary placeholder:font-normal placeholder:text-text-tertiary focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                />
                {parsedMonthlyContribution > 0 && parsedMonthlyContribution >= parsedTargetAmount && (
                  <p className="mt-1 text-xs text-error">
                    O aporte mensal deve ser menor que o valor alvo.
                  </p>
                )}
              </div>
            </div>

            <div className="lg:hidden">{previewNode}</div>
          </div>

          {/* Section 2: Entity Linking */}
          <div className="space-y-5">
            <div className="mb-3 flex items-center gap-2">
              <span className="flex size-8 items-center justify-center rounded-full bg-surface-sunken text-sm font-bold text-text-secondary">
                02
              </span>
              <h3 className="text-lg font-bold text-text-primary">Associação de Ativos</h3>
            </div>

            <div className="rounded-2xl border border-border-subtle bg-surface-sunken p-4 sm:p-6">
              <p className="mb-5 text-sm/relaxed text-text-secondary">
                O progresso desta meta será rastreado{' '}
                <span className="font-semibold text-success-text">automaticamente</span> a partir
                do saldo consolidado das entidades selecionadas abaixo.
              </p>

              {linkableEntities.length === 0 ? (
                <p className="py-8 text-center text-sm text-text-tertiary">
                  Nenhuma conta ou investimento disponível.
                </p>
              ) : (
                <div className="space-y-3">
                  {linkableEntities.map((entity) => {
                    const isChecked = selectedEntityIds.has(entity.id);
                    const isTakenByOther = entity.linkedGoalId !== null && entity.linkedGoalId !== goalId;
                    const isDisabled = isTakenByOther && !isChecked;
                    return (
                      <label
                        key={entity.id}
                        className={`flex items-center justify-between gap-3 p-3 sm:p-4 bg-surface rounded-xl transition-all ${
                          isDisabled
                            ? 'opacity-50 cursor-not-allowed'
                            : 'cursor-pointer hover:shadow-none'
                        } ${isChecked ? 'ring-2 ring-success/30' : ''}`}
                        onClick={(e) => { if (isDisabled) e.preventDefault(); }}
                      >
                        <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
                          <div
                            className={`size-10 shrink-0 rounded-lg flex items-center justify-center ${
                              entity.type === 'account'
                                ? 'bg-purple-100 text-purple-600'
                                : 'bg-error-surface text-error-text'
                            }`}
                          >
                            {entity.type === 'account' ? (
                              <BuildingsIcon className="size-5" />
                            ) : (
                              <TrendUpIcon className="size-5" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-bold text-text-primary">{entity.name}</p>
                            <p className="truncate font-mono text-xs text-text-secondary">
                              {formatCurrency(entity.balance)}
                              {entity.institution && ` • ${entity.institution}`}
                            </p>
                            {entity.isWithdrawn && (
                              <p className="mt-0.5 truncate text-xs text-text-tertiary">
                                Resgatado — não conta mais para a meta. Desmarque para desvincular.
                              </p>
                            )}
                            {isTakenByOther && (
                              <p className="mt-0.5 truncate text-xs text-warning-text">
                                Vinculado à meta: {entity.linkedGoalName}
                              </p>
                            )}
                          </div>
                        </div>
                        <div
                          className={`size-5 shrink-0 rounded border-2 flex items-center justify-center transition-colors ${
                            isChecked
                              ? 'bg-success border-success-border'
                              : isDisabled
                                ? 'border-border-subtle bg-muted'
                                : 'border-border-strong'
                          }`}
                          onClick={() => { if (!isDisabled) toggleEntity(entity.id); }}
                        >
                          {isChecked && <CheckIcon className="size-3 text-success-foreground" />}
                        </div>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={isDisabled}
                          onChange={() => { if (!isDisabled) toggleEntity(entity.id); }}
                          className="sr-only"
                        />
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:flex-wrap sm:items-center">
            <Button
              size="lg"
              onClick={handleSave}
              disabled={saving || !name || parsedTargetAmount <= 0 || (parsedMonthlyContribution > 0 && parsedMonthlyContribution >= parsedTargetAmount) || (!!targetDate && targetDate < new Date().toISOString().split('T')[0])}
              className="w-full sm:w-auto"
            >
              {saving ? 'Salvando...' : isEditing ? 'Salvar' : 'Salvar Meta'}
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => router.push('/metas')}
              className="w-full sm:w-auto"
            >
              Cancelar
            </Button>
            {isEditing && (
              <Button
                size="lg"
                variant="outline"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={saving}
                className="w-full border-error-border text-error-text hover:bg-error-surface hover:text-error-text sm:ml-auto sm:w-auto"
              >
                <TrashIcon className="size-4" />
                Excluir
              </Button>
            )}

            <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Excluir meta</DialogTitle>
                  <DialogDescription>
                    Tem certeza que deseja excluir a meta <strong>{name}</strong>? Esta ação não pode ser desfeita.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="justify-end gap-2 sm:gap-0">
                  <Button
                    variant="ghost"
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleDelete}
                    disabled={saving}
                    className="bg-error text-destructive-foreground hover:bg-error"
                  >
                    {saving ? 'Excluindo...' : 'Confirmar Exclusão'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Right: Preview (desktop only) */}
        <aside className="hidden lg:sticky lg:top-8 lg:col-span-5 lg:block">
          {previewNode}
        </aside>
      </div>
    </div>
  );
}
